import { useState, useEffect, useRef, useCallback } from "react";

// ── Constants ────────────────────────────────────────────────────────────────
// CHANGE 1: HISTORY_SEC 300 → 86400 (5min → 24h)
const HISTORY_SEC   = 86400;
const PRICE_AXIS_W  = 72;
const TIME_AXIS_H   = 36;
const COB_W         = 188;
const VOL_H         = 68;
const SNAP_MS       = 500;
// SNAP_RANGE is now per-pair — see pairCfgRef below
const RAF_TARGET_MS = 32;
const HM_REBUILD_MS = 66;
const MAX_TRADE_BUF = 15000;   // reduced from 60k — ~2h of BTC flow is enough

// CHANGE 2: MAX_SNAPS 800 → 4000 (hot ring buffer, last ~33min at 500ms)
const MAX_SNAPS     = 2000;    // reduced from 4000 — hot ring = ~17min at 500ms

// CHANGE 3a: New constants for downsampling + lazy paging
const ARCHIVE_SNAPS  = 2000;   // reduced from 4000
const DOWNSAMPLE_AGE = 120000; // compress snaps older than 2min (was 10min)
const DOWNSAMPLE_BIN = 5000;   // merge into 5s buckets (was 10s)
const PAGE_MS        = 300000; // load DB in 5min pages when panning

// ── IndexedDB persistence ─────────────────────────────────────────────────────
const DB_VER     = 2;
const PERSIST_MS = 8000;
const MAX_DB_AGE = 86400000; // 24h

// Per-symbol DB names — each pair gets its own isolated database
const getDBName = (sym) => `liquidmap_${sym}`;

// Per-symbol DB connection cache
const _dbCache = new Map();
function getDB(sym) {
  if (_dbCache.has(sym)) return _dbCache.get(sym);
  const p = new Promise((res, rej) => {
    const req = indexedDB.open(getDBName(sym), DB_VER);
    req.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains("snaps"))   db.createObjectStore("snaps",   { keyPath: "time" });
      if (!db.objectStoreNames.contains("trades"))  db.createObjectStore("trades",  { keyPath: "time" });
      if (!db.objectStoreNames.contains("vol"))     db.createObjectStore("vol",     { keyPath: "time" });
      if (!db.objectStoreNames.contains("snaps2"))  db.createObjectStore("snaps2",  { keyPath: "time" });
    };
    req.onsuccess  = e => res(e.target.result);
    req.onerror    = () => { _dbCache.delete(sym); rej(req.error); };
    req.onblocked  = () => { _dbCache.delete(sym); rej(new Error("DB blocked")); };
  });
  _dbCache.set(sym, p);
  return p;
}

const BG_R = 5, BG_G = 9, BG_B = 18;

// ── Pre-computed colour LUT ───────────────────────────────────────────────────
const BID_LUT = new Uint8Array(256 * 3);
const ASK_LUT = new Uint8Array(256 * 3);

for (let i = 0; i < 256; i++) {
  const t = i / 255;
  const a = t < 0.08 ? 0 : Math.min(0.96, ((t - 0.08) / 0.92) * 0.96);
  const ia = 1 - a;
  let r, g, b;
  if (t < 0.08) { r = BG_R; g = BG_G; b = BG_B; }
  else if (t < 0.40) { const s=(t-0.08)/0.32; r=s*6; g=s*15; b=s*80; }
  else if (t < 0.65) { const s=(t-0.40)/0.25; r=6+s*14; g=15+s*105; b=80+s*140; }
  else if (t < 0.85) { const s=(t-0.65)/0.20; r=20+s*10; g=120+s*100; b=220+s*30; }
  else               { const s=(t-0.85)/0.15; r=30+s*10; g=220+s*35; b=250+s*5; }
  BID_LUT[i*3]   = Math.min(255, Math.round(r*a + BG_R*ia));
  BID_LUT[i*3+1] = Math.min(255, Math.round(g*a + BG_G*ia));
  BID_LUT[i*3+2] = Math.min(255, Math.round(b*a + BG_B*ia));

  if (t < 0.08) { r = BG_R; g = BG_G; b = BG_B; }
  else if (t < 0.40) { const s=(t-0.08)/0.32; r=s*80; g=s*6; b=0; }
  else if (t < 0.65) { const s=(t-0.40)/0.25; r=80+s*150; g=6+s*54; b=0; }
  else if (t < 0.85) { const s=(t-0.65)/0.20; r=230+s*20; g=60+s*130; b=s*10; }
  else               { const s=(t-0.85)/0.15; r=250+s*5; g=190+s*40; b=10+s*10; }
  ASK_LUT[i*3]   = Math.min(255, Math.round(r*a + BG_R*ia));
  ASK_LUT[i*3+1] = Math.min(255, Math.round(g*a + BG_G*ia));
  ASK_LUT[i*3+2] = Math.min(255, Math.round(b*a + BG_B*ia));
}

// ── Binary search ─────────────────────────────────────────────────────────────
function lowerBound(arr, target) {
  let lo = 0, hi = arr.length;
  while (lo < hi) { const mid = (lo+hi)>>>1; if (arr[mid].time < target) lo=mid+1; else hi=mid; }
  return lo;
}

// ── Direct ArrayBuffer storage — no encode/decode needed ─────────────────────
// IndexedDB natively supports ArrayBuffer. Storing Float32Array.buffer directly
// is ~10x faster than btoa/atob on the main thread.
function packSnap(snap) {
  return { time: snap.time, mid: snap.mid, buf: snap.levels.buffer.slice(0) };
}
function unpackSnap(row) {
  return {
    time: row.time,
    mid:  row.mid,
    // Handle legacy base64 rows saved by old code
    levels: row.buf instanceof ArrayBuffer
      ? new Float32Array(row.buf)
      : typeof row.levels === "string"
        ? decodeF32Legacy(row.levels)
        : row.levels instanceof Float32Array
          ? row.levels
          : new Float32Array(row.levels),
  };
}
// Keep legacy decoder for rows saved by old code
function decodeF32Legacy(b64) {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

// ── IndexedDB helpers — symbol-aware ─────────────────────────────────────────
async function dbPutBatch(sym, storeName, rows) {
  if (!rows.length) return;
  try {
    const db = await getDB(sym);
    const tx = db.transaction(storeName, "readwrite");
    const st = tx.objectStore(storeName);
    for (const r of rows) st.put(r);
    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
  } catch (e) { console.warn("DB write:", e); _dbCache.delete(sym); }
}

async function dbLoadRange(sym, storeName, fromTime, limit = 5000) {
  try {
    const db    = await getDB(sym);
    const tx    = db.transaction(storeName, "readonly");
    const st    = tx.objectStore(storeName);
    const range = IDBKeyRange.lowerBound(fromTime);
    const rows  = await new Promise((res, rej) => {
      const req = st.getAll(range, limit);
      req.onsuccess = () => res(req.result);
      req.onerror   = rej;
    });
    return rows || [];
  } catch (e) { console.warn("DB read:", e); _dbCache.delete(sym); return []; }
}

async function dbDeleteOlderThan(sym, storeName, cutoff) {
  try {
    const db    = await getDB(sym);
    const tx    = db.transaction(storeName, "readwrite");
    const st    = tx.objectStore(storeName);
    const range = IDBKeyRange.upperBound(cutoff, true);
    st.delete(range);
    await new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
  } catch (_) { _dbCache.delete(_); }
}

// Count rows in a store (fast — uses IDBObjectStore.count)
async function dbCountStore(sym, storeName) {
  try {
    const db = await getDB(sym);
    const tx = db.transaction(storeName, "readonly");
    const st = tx.objectStore(storeName);
    return await new Promise((res, rej) => {
      const req = st.count();
      req.onsuccess = () => res(req.result);
      req.onerror   = () => res(0);
    });
  } catch (_) { return 0; }
}

// Delete all rows from every store for a symbol, then close + delete the entire DB
async function dbClearSymbol(sym) {
  try {
    // Close cached connection first so deleteDatabase can proceed
    const p = _dbCache.get(sym);
    if (p) { try { const db = await p; db.close(); } catch (_) {} }
    _dbCache.delete(sym);
    await new Promise((res, rej) => {
      const req = indexedDB.deleteDatabase(getDBName(sym));
      req.onsuccess = res; req.onerror = rej; req.onblocked = res;
    });
  } catch (e) { console.warn("DB clear failed:", e); }
}

// Get first and last key (timestamp) from a store
async function dbGetTimeRange(sym, storeName) {
  try {
    const db = await getDB(sym);
    const tx = db.transaction(storeName, "readonly");
    const st = tx.objectStore(storeName);
    const [first, last] = await Promise.all([
      new Promise(res => { const r = st.openCursor(null, "next");    r.onsuccess = () => res(r.result?.key ?? null); r.onerror = () => res(null); }),
      new Promise(res => { const r = st.openCursor(null, "prev");    r.onsuccess = () => res(r.result?.key ?? null); r.onerror = () => res(null); }),
    ]);
    return { first, last };
  } catch (_) { return { first: null, last: null }; }
}

// Estimate bytes: snaps ~2.5KB each (Float32Array levels), trades ~120B, vol ~60B
function estimateBytes(snaps, trades, vol) {
  return snaps * 2500 + trades * 120 + vol * 60;
}
function fmtBytes(bytes) {
  if (bytes >= 1e9) return (bytes/1e9).toFixed(2) + " GB";
  if (bytes >= 1e6) return (bytes/1e6).toFixed(1) + " MB";
  if (bytes >= 1e3) return (bytes/1e3).toFixed(0) + " KB";
  return bytes + " B";
}
function fmtRange(first, last) {
  if (!first || !last) return "—";
  const fmt = ts => {
    const d = new Date(ts);
    return d.toLocaleString("en-US", { hour12:false, month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit" });
  };
  const durMs = last - first;
  const durH  = durMs / 3600000;
  const durStr = durH >= 1 ? `${durH.toFixed(1)}h` : `${Math.round(durMs/60000)}m`;
  return `${fmt(first)} → ${fmt(last)}  (${durStr})`;
}

// Gather stats for all pairs — counts + time range + size estimate
async function dbGetAllStats() {
  const results = [];
  for (const pair of PAIRS) {
    const sym = pair.symbol;
    const [snapCount, tradeCount, volCount, snapRange, tradeRange] = await Promise.all([
      dbCountStore(sym, "snaps2"),
      dbCountStore(sym, "trades"),
      dbCountStore(sym, "vol"),
      dbGetTimeRange(sym, "snaps2"),
      dbGetTimeRange(sym, "trades"),
    ]);
    const total = snapCount + tradeCount + volCount;
    if (total === 0) continue;
    // Use snap range as primary, fall back to trades
    const firstTs = snapRange.first ?? tradeRange.first;
    const lastTs  = snapRange.last  ?? tradeRange.last;
    const estBytes = estimateBytes(snapCount, tradeCount, volCount);
    results.push({ sym, label: pair.label, snaps: snapCount, trades: tradeCount, vol: volCount, total, firstTs, lastTs, estBytes });
  }
  // Also get real storage estimate from browser if available
  let realUsage = null;
  try { const est = await navigator.storage.estimate(); realUsage = est.usage ?? null; } catch (_) {}
  return { rows: results, realUsage };
}

const fmtP = (p, decimals=1) => p.toLocaleString("en-US", { minimumFractionDigits:decimals, maximumFractionDigits:decimals });
const fmtT = (ts, visMs) => {
  const d = new Date(ts);
  if (visMs > 3600000) {
    // Zoomed out to hours/days — show date + hour:min
    return d.toLocaleString("en-US", { hour12:false, month:"2-digit", day:"2-digit", hour:"2-digit", minute:"2-digit" });
  }
  if (visMs < 10000) {
    // Zoomed into seconds — show HH:MM:SS.d
    return d.toLocaleTimeString("en-US", { hour12:false, hour:"2-digit", minute:"2-digit", second:"2-digit" })
      + "." + String(Math.floor(d.getMilliseconds()/100));
  }
  return d.toLocaleTimeString("en-US", { hour12:false, hour:"2-digit", minute:"2-digit", second:"2-digit" });
};
const fmtQ = q => q >= 1000 ? (q/1000).toFixed(1)+"k" : q >= 100 ? q.toFixed(0) : q >= 10 ? q.toFixed(1) : q.toFixed(2);

// ── Trade bubble ──────────────────────────────────────────────────────────────
function drawBubble(ctx, x, y, r, buy) {
  const R = Math.min(Math.max(r, 2), 28);
  const gl = ctx.createRadialGradient(x,y,R*0.15,x,y,R*2);
  gl.addColorStop(0,   buy?"rgba(0,255,120,0.25)":"rgba(255,60,50,0.25)");
  gl.addColorStop(0.5, buy?"rgba(0,200,80,0.06)":"rgba(255,40,30,0.06)");
  gl.addColorStop(1,   "rgba(0,0,0,0)");
  ctx.fillStyle=gl; ctx.beginPath(); ctx.arc(x,y,R*2,0,Math.PI*2); ctx.fill();
  const g = ctx.createRadialGradient(x-R*0.28,y-R*0.28,R*0.04,x,y,R);
  if (buy) {
    g.addColorStop(0,"rgba(190,255,215,0.95)"); g.addColorStop(0.35,"rgba(55,235,115,0.9)");
    g.addColorStop(0.7,"rgba(15,180,65,0.85)"); g.addColorStop(1,"rgba(0,55,18,0.7)");
  } else {
    g.addColorStop(0,"rgba(255,205,185,0.95)"); g.addColorStop(0.35,"rgba(255,95,75,0.9)");
    g.addColorStop(0.7,"rgba(218,45,25,0.85)"); g.addColorStop(1,"rgba(78,8,4,0.7)");
  }
  ctx.fillStyle=g; ctx.beginPath(); ctx.arc(x,y,R,0,Math.PI*2); ctx.fill();
  if (R>5) {
    const s=ctx.createRadialGradient(x-R*0.3,y-R*0.3,0,x-R*0.18,y-R*0.18,R*0.5);
    s.addColorStop(0,"rgba(255,255,255,0.78)"); s.addColorStop(1,"rgba(255,255,255,0)");
    ctx.fillStyle=s; ctx.beginPath(); ctx.arc(x,y,R,0,Math.PI*2); ctx.fill();
  }
  ctx.strokeStyle=buy?"rgba(0,90,35,0.22)":"rgba(110,18,8,0.22)";
  ctx.lineWidth=0.6; ctx.beginPath(); ctx.arc(x,y,R,0,Math.PI*2); ctx.stroke();
}

// ── Simulated feed ────────────────────────────────────────────────────────────
class SimFeed {
  constructor(onTrade, onDepth) {
    this.onTrade=onTrade; this.onDepth=onDepth;
    this.price=97500; this.iv=null; this.connected=true;
    this.walls=Array.from({length:16},(_,k)=>{
      const side=k%2===0?"bid":"ask";
      const dist=(Math.floor(Math.random()*10)+1)*100;
      return { side, basePrice:side==="bid"?97500-dist:97500+dist, size:15+Math.random()*60, life:100+Math.random()*500 };
    });
  }
  connect() {
    this.iv=setInterval(()=>{
      this.price+=(Math.random()-0.499)*(1.5+Math.random()*5);
      this.price=Math.max(93000,Math.min(102000,this.price));
      if (Math.random()<0.7) {
        const side=Math.random()>0.5?"buy":"sell";
        const qty=Math.random()<0.04?1+Math.random()*5:0.001+Math.random()*0.1;
        this.onTrade({price:this.price+(Math.random()-0.5)*1.5,qty,side,time:Date.now()});
      }
      const bids=new Map(), asks=new Map();
      for (let i=0;i<500;i++) {
        const off=i*0.5+Math.random()*1;
        bids.set(parseFloat((this.price-0.5-off).toFixed(1)),Math.max(0.001,Math.random()*(i<20?1:i<100?3:8)));
        asks.set(parseFloat((this.price+0.5+off).toFixed(1)),Math.max(0.001,Math.random()*(i<20?1:i<100?3:8)));
      }
      for (const w of this.walls) {
        w.life--;
        if (w.life<=0) {
          const side=Math.random()>0.5?"bid":"ask";
          const dist=(Math.floor(Math.random()*12)+1)*100;
          w.side=side; w.size=15+Math.random()*70; w.life=150+Math.random()*600;
          w.basePrice=side==="bid"?this.price-dist:this.price+dist;
        }
        w.size+=(Math.random()-0.5)*3;
        w.size=Math.max(8,Math.min(120,w.size));
        const p=parseFloat(w.basePrice.toFixed(1));
        if (w.side==="bid"&&p<this.price) bids.set(p,w.size+Math.random()*4);
        if (w.side==="ask"&&p>this.price) asks.set(p,w.size+Math.random()*4);
      }
      this.onDepth({bids,asks});
    }, SNAP_MS);
  }
  disconnect() { clearInterval(this.iv); this.connected=false; }
}

// ── Binance Futures feed ──────────────────────────────────────────────────────
class BinanceFeed {
  constructor(symbol, snapRange, onTrade, onDepth) {
    this.symbol   = symbol.toLowerCase();
    this.SYMBOL   = symbol.toUpperCase();
    this.snapRange = snapRange;  // per-pair capture range
    this.onTrade=onTrade; this.onDepth=onDepth;
    this.ws=null; this.dws=null; this.connected=false;
    this.ob={bids:new Map(),asks:new Map()};
    this.lastUpdateId=0; this.snapshotReady=false; this.diffBuffer=[];
    this.lastPushMs=0;
    this.lastPruneMs=0;
    this._dead=false;
    this._tradeWsReconnectTimer=null;
    this._depthWsReconnectTimer=null;
  }

  connect() {
    this._dead = false;
    this._lastTradeMs = Date.now();
    this._connectTrade();
    this._connectDepth();
    // Heartbeat: if no trade received for 30s, the trade WS is zombie — reconnect it
    this._heartbeatTimer = setInterval(() => {
      if (this._dead) return;
      if (Date.now() - this._lastTradeMs > 30000) {
        console.warn(`💔 [${this.SYMBOL}] Trade WS silent >30s — forcing reconnect`);
        try { this.ws?.close(); } catch (_) {}
        this._connectTrade();
      }
    }, 10000);
  }

  _connectTrade() {
    if (this._dead) return;
    clearTimeout(this._tradeWsReconnectTimer);
    try {
      this.ws = new WebSocket(`wss://fstream.binance.com/ws/${this.symbol}@aggTrade`);
      this.ws.onmessage = e => {
        this._lastTradeMs = Date.now();
        const d = JSON.parse(e.data);
        this.onTrade({price:parseFloat(d.p),qty:parseFloat(d.q),side:d.m?"sell":"buy",time:d.T});
      };
      this.ws.onopen  = () => { this.connected = true; this._lastTradeMs = Date.now(); };
      this.ws.onclose = () => { if (!this._dead) this._tradeWsReconnectTimer = setTimeout(()=>this._connectTrade(), 3000); };
      // onerror: close the socket so onclose fires and triggers reconnect
      this.ws.onerror = () => { this.connected = false; try { this.ws?.close(); } catch(_){} };
    } catch (e) { this.connected = false; if (!this._dead) this._tradeWsReconnectTimer = setTimeout(()=>this._connectTrade(), 3000); }
  }

  _connectDepth() {
    if (this._dead) return;
    clearTimeout(this._depthWsReconnectTimer);
    try {
      this.snapshotReady = false; this.diffBuffer = [];
      this.dws = new WebSocket(`wss://fstream.binance.com/ws/${this.symbol}@depth@500ms`);
      this.dws.onopen    = () => { this.connected = true; this._fetchSnapshot(); };
      this.dws.onmessage = e => {
        const d = JSON.parse(e.data);
        if (!this.snapshotReady) {
          this.diffBuffer.push(d);
          // Cap diffBuffer — if snapshot is taking too long, old diffs are useless anyway
          if (this.diffBuffer.length > 200) this.diffBuffer.splice(0, 100);
          return;
        }
        this._applyDiff(d);
      };
      this.dws.onclose = () => { if (!this._dead) this._depthWsReconnectTimer = setTimeout(()=>this._connectDepth(), 3000); };
      this.dws.onerror = () => { this.connected = false; try { this.dws?.close(); } catch(_){} };
    } catch (e) { this.connected = false; if (!this._dead) this._depthWsReconnectTimer = setTimeout(()=>this._connectDepth(), 3000); }
  }

  async _fetchSnapshot() {
    try {
      const res  = await fetch(`https://fapi.binance.com/fapi/v1/depth?symbol=${this.SYMBOL}&limit=500`);
      const snap = await res.json();
      this.ob.bids.clear(); this.ob.asks.clear();
      snap.bids.forEach(([p,q])=>this.ob.bids.set(parseFloat(p),parseFloat(q)));
      snap.asks.forEach(([p,q])=>this.ob.asks.set(parseFloat(p),parseFloat(q)));
      this.lastUpdateId=snap.lastUpdateId;
      for (const d of this.diffBuffer) { if (d.u<=this.lastUpdateId) continue; this._applyDiff(d); }
      this.diffBuffer=[]; this.snapshotReady=true;
    } catch (e) { console.error("Snapshot failed:", e); setTimeout(()=>this._fetchSnapshot(),2000); }
  }

  _applyDiff(d) {
    (d.b||[]).forEach(([p,q])=>{ const pf=parseFloat(p),qf=parseFloat(q); qf===0?this.ob.bids.delete(pf):this.ob.bids.set(pf,qf); });
    (d.a||[]).forEach(([p,q])=>{ const pf=parseFloat(p),qf=parseFloat(q); qf===0?this.ob.asks.delete(pf):this.ob.asks.set(pf,qf); });

    const now = Date.now();

    if (now - this.lastPruneMs > 10000 && this.ob.bids.size > 0) {
      this.lastPruneMs = now;
      let bestBid = 0, bestAsk = Infinity;
      this.ob.bids.forEach((_,p)=>{ if (p>bestBid) bestBid=p; });
      this.ob.asks.forEach((_,p)=>{ if (p<bestAsk) bestAsk=p; });
      const midEst = (bestBid + bestAsk) / 2 || bestBid || bestAsk;
      if (midEst > 0) {
        const PRUNE_RANGE = this.snapRange * 1.5;
        this.ob.bids.forEach((_,p)=>{ if (p < midEst - PRUNE_RANGE) this.ob.bids.delete(p); });
        this.ob.asks.forEach((_,p)=>{ if (p > midEst + PRUNE_RANGE) this.ob.asks.delete(p); });
      }
    }

    if (now - this.lastPushMs >= SNAP_MS) {
      this.lastPushMs = now;
      if (this.ob.bids.size > 0)
        this.onDepth({ bids: this.ob.bids, asks: this.ob.asks });
    }
  }

  disconnect() {
    this._dead = true;
    clearTimeout(this._tradeWsReconnectTimer);
    clearTimeout(this._depthWsReconnectTimer);
    clearInterval(this._heartbeatTimer);
    try { this.ws?.close(); } catch (_) {}
    try { this.dws?.close(); } catch (_) {}
    this.connected = false; this.snapshotReady = false;
  }
}

// ── Supported trading pairs — all scale values are per-pair ──────────────────
// snapRange  : how far ± from mid to capture order book (in price units)
// defaultPRange: the default visible price window height at pZoom=1
// volRange   : ± range for bid/ask imbalance calculation
// gridSteps  : price grid tick sizes [largest→smallest]
// decimals   : decimal places for price display
const PAIRS = [
  { symbol:"BTCUSDT",  label:"BTC",  snapRange:8000, defaultPRange:2000, volRange:500,  gridSteps:[5000,2000,1000,500,200,100,50,20,10],  decimals:1 },
  { symbol:"ETHUSDT",  label:"ETH",  snapRange:400,  defaultPRange:100,  volRange:30,   gridSteps:[500,200,100,50,20,10,5,2,1],            decimals:2 },
  { symbol:"SOLUSDT",  label:"SOL",  snapRange:25,   defaultPRange:6,    volRange:2,    gridSteps:[50,20,10,5,2,1,0.5,0.2,0.1],            decimals:2 },
  { symbol:"BNBUSDT",  label:"BNB",  snapRange:80,   defaultPRange:20,   volRange:5,    gridSteps:[200,100,50,20,10,5,2,1,0.5],            decimals:2 },
  { symbol:"XRPUSDT",  label:"XRP",  snapRange:0.8,  defaultPRange:0.2,  volRange:0.05, gridSteps:[2,1,0.5,0.2,0.1,0.05,0.02,0.01,0.005], decimals:4 },
  { symbol:"DOGEUSDT", label:"DOGE", snapRange:0.05, defaultPRange:0.015,volRange:0.003,gridSteps:[0.2,0.1,0.05,0.02,0.01,0.005,0.002,0.001,0.0005],decimals:5 },
];
const getPairCfg = (sym) => PAIRS.find(p=>p.symbol===sym) || PAIRS[0];

// ── Main component ────────────────────────────────────────────────────────────
export default function LiquidityHeatmap() {
  const [isLive,    setIsLive]    = useState(true);
  const [intensity, setIntensity] = useState(1.0);
  const [source,    setSource]    = useState("binance");
  const [status,    setStatus]    = useState("connecting");
  const [curPrice,  setCurPrice]  = useState(0);
  const [pChange,   setPChange]   = useState(0);
  const [trades24,  setTrades24]  = useState(0);
  const [tZoom,     setTZoom]     = useState(1);
  const [pZoom,     setPZoom]     = useState(1);
  const [pOff,      setPOff]      = useState(0);
  const [tOff,      setTOff]      = useState(0);
  const [obLevels,  setObLevels]  = useState(0);
  const [imbalance, setImbalance] = useState(0.5);
  const [activeWindow, setActiveWindow] = useState("5m");
  // Multi-pair
  const [symbol,    setSymbol]    = useState("BTCUSDT");
  const symbolRef   = useRef("BTCUSDT");
  // Per-pair scale config — updated whenever symbol changes
  const pairCfgRef  = useRef(getPairCfg("BTCUSDT"));
  // History load state — shows progress to user
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadedHours,    setLoadedHours]    = useState(0);
  // Storage panel
  const [showStorage,   setShowStorage]   = useState(false);
  const [storageStats,  setStorageStats]  = useState([]);
  const [clearingAll,   setClearingAll]   = useState(false);

  const hRef   = useRef(null);
  const vRef   = useRef(null);
  const cobRef = useRef(null);
  const hmGeom = useRef({ W:0, H:0, startT:0, visMs:0, minP:0, pRange:0 });

  const feedRef    = useRef(null);
  const afRef      = useRef(null);
  const hmData     = useRef([]);
  const tradesBuf  = useRef([]);
  const curOB      = useRef({ bids:new Map(), asks:new Map() });
  const mid        = useRef(0);
  const prevMid    = useRef(0);
  const tc         = useRef(0);
  const volHist    = useRef([]);
  const liveRef    = useRef(true);
  const intRef     = useRef(1.0);

  // ── Canvas context cache ──────────────────────────────────────────────────
  const hctxRef = useRef(null);
  const vctxRef = useRef(null);
  const cctxRef = useRef(null);

  // ── Heatmap pixel cache ───────────────────────────────────────────────────
  const hmDirty       = useRef(true);
  const cachedImg     = useRef(null);
  const lastRenderKey = useRef("");
  const lastHmTime    = useRef(0);
  const lastRafTime   = useRef(0);

  // ── Pre-allocated typed arrays ────────────────────────────────────────────
  const rowLI_bid_ref = useRef(null);
  const rowLI_ask_ref = useRef(null);
  const rowGN_bid_ref = useRef(null);
  const rowGN_ask_ref = useRef(null);
  const usedRowsRef   = useRef(new Int32Array(4000));
  const tradeUsedRef  = useRef(new Uint8Array(MAX_TRADE_BUF));
  const sampleArrRef  = useRef(new Float32Array(MAX_TRADE_BUF));

  // ── Normalization ─────────────────────────────────────────────────────────
  const normRef     = useRef(0);
  const cobFrame    = useRef(0);
  const visRangeRef = useRef(getPairCfg("BTCUSDT").snapRange);

  // ── Pan state ─────────────────────────────────────────────────────────────
  const panFlag     = useRef(false);
  const panAxis     = useRef(null);
  const panStart    = useRef({ x:0, y:0 });
  const paxFlag     = useRef(false);
  const paxStartY   = useRef(0);
  const paxStartOff = useRef(0);

  // CHANGE 4a: Refs for lazy DB loading
  const dbLoadingRef   = useRef(false);
  const loadedRangeRef = useRef({ from: Date.now() - HISTORY_SEC * 1000, to: Date.now() });

  useEffect(() => { liveRef.current = isLive;    }, [isLive]);
  useEffect(() => { intRef.current  = intensity; }, [intensity]);
  useEffect(() => {
    symbolRef.current  = symbol;
    pairCfgRef.current = getPairCfg(symbol);
    // Reset price zoom to 1 so defaultPRange is used fresh for the new pair
    setPZoom(1);
    setPOff(0);
  }, [symbol]);

  // ── Load a specific range of history from DB into memory ──────────────────
  // This is the "Load 1h / 5h / 12h / 24h" button handler.
  // Unlike lazy paging (which loads one page as you pan), this bulk-loads
  // the entire requested range upfront so panning is instant afterwards.
  const loadHistoryRange = useCallback(async (hours) => {
    if (loadingHistory) return;
    const sym = symbolRef.current;
    const now = Date.now();
    const fromTime = now - hours * 3600000;
    setLoadingHistory(true);
    setLoadedHours(0);
    try {
      const pageSize = PAGE_MS; // 5min pages
      const totalPages = Math.ceil((hours * 3600000) / pageSize);
      let allSnaps = [], allTrades = [];

      // Load in 5min chunks so we can show progress
      for (let page = 0; page < totalPages; page++) {
        const pageFrom = fromTime + page * pageSize;
        const pageTo   = pageFrom + pageSize;
        const [sRows, tRows] = await Promise.all([
          dbLoadRange(sym, "snaps2", pageFrom, 1000)
            .then(r => r.length ? r : dbLoadRange(sym, "snaps", pageFrom, 1000)),
          dbLoadRange(sym, "trades", pageFrom, 10000),
        ]);
        allSnaps  = allSnaps.concat(sRows.filter(r => r.time < pageTo));
        allTrades = allTrades.concat(tRows.filter(r => r.time < pageTo));
        setLoadedHours(Math.min(hours, ((page + 1) / totalPages) * hours));
        // Yield to browser between pages so UI doesn't freeze
        await new Promise(r => setTimeout(r, 0));
      }

      if (allSnaps.length) {
        const decoded = allSnaps.map(unpackSnap);
        decoded.sort((a,b)=>a.time-b.time);
        // Merge with existing live data (keep newest end)
        const existing = hmData.current;
        const earliest = decoded[0].time;
        const keepIdx  = lowerBound(existing, earliest);
        hmData.current = [...decoded, ...existing.slice(keepIdx)]
          .slice(-(ARCHIVE_SNAPS + MAX_SNAPS));
        hmDirty.current = true;
        normRef.current = 0; // recompute norm for new data range
      }

      if (allTrades.length) {
        const existing = tradesBuf.current;
        const earliest = allTrades[0]?.time ?? Infinity;
        const keepIdx  = lowerBound(existing, earliest);
        tradesBuf.current = [...allTrades, ...existing.slice(keepIdx)];
        if (tradesBuf.current.length > MAX_TRADE_BUF)
          tradesBuf.current.splice(0, tradesBuf.current.length - MAX_TRADE_BUF);
      }

      // Jump view to show the loaded range
      const newZoom = HISTORY_SEC / (hours * 3600);
      setTZoom(Math.max(0.1, newZoom));
      setTOff(0);
      setIsLive(false);
      setLoadedHours(hours);
      console.log(`📦 Loaded ${hours}h: ${allSnaps.length} snaps, ${allTrades.length} trades`);
    } catch (e) {
      console.warn("loadHistoryRange failed:", e);
    } finally {
      setLoadingHistory(false);
    }
  }, [loadingHistory]);

  // ── Switch trading pair ───────────────────────────────────────────────────
  const switchSymbol = useCallback(async (newSym) => {
    if (newSym === symbolRef.current) return;
    // Disconnect current feed
    if (feedRef.current) { feedRef.current.disconnect(); feedRef.current = null; }
    // Clear all in-memory buffers
    hmData.current     = [];
    tradesBuf.current  = [];
    volHist.current    = [];
    curOB.current      = { bids:new Map(), asks:new Map() };
    mid.current        = 0;
    prevMid.current    = 0;
    tc.current         = 0;
    normRef.current    = 0;
    cachedImg.current  = null;
    hmDirty.current    = true;
    lastSavedTime.current      = 0;
    lastSavedTradeTime.current = 0;
    loadedRangeRef.current     = { from: Date.now() - HISTORY_SEC * 1000, to: Date.now() };
    setCurPrice(0); setPChange(0); setTrades24(0); setObLevels(0);
    setTOff(0); setPOff(0); setTZoom(1); setIsLive(true);
    setActiveWindow("5m"); setLoadedHours(0);
    setSymbol(newSym);
    symbolRef.current = newSym;
    setSource("binance"); // triggers feed useEffect
  }, []);

  // CHANGE 4b: Lazy DB page loader — symbol-aware
  const loadPageFromDB = useCallback(async (targetTime) => {
    if (dbLoadingRef.current) return;
    const sym  = symbolRef.current;
    const data = hmData.current;
    if (data.length && targetTime >= data[0].time) return;

    dbLoadingRef.current = true;
    try {
      const pageFrom = targetTime - PAGE_MS;
      const [snapRows, tradeRows] = await Promise.all([
        dbLoadRange(sym, "snaps2", pageFrom, 2000)
          .then(rows => rows.length ? rows : dbLoadRange(sym, "snaps", pageFrom, 2000)),
        dbLoadRange(sym, "trades", pageFrom, 20000),
      ]);

      if (snapRows.length) {
        const decoded = snapRows
          .filter(r => r.time < (data[0]?.time ?? Infinity))
          .map(unpackSnap);
        if (decoded.length) {
          hmData.current = [...decoded, ...hmData.current].slice(-(ARCHIVE_SNAPS + MAX_SNAPS));
          hmDirty.current = true;
        }
      }
      if (tradeRows.length) {
        const existing = tradesBuf.current;
        const earliestExisting = existing[0]?.time ?? Infinity;
        const newTrades = tradeRows.filter(t => t.time < earliestExisting);
        if (newTrades.length) {
          tradesBuf.current = [...newTrades, ...existing];
          if (tradesBuf.current.length > MAX_TRADE_BUF)
            tradesBuf.current.splice(0, tradesBuf.current.length - MAX_TRADE_BUF);
        }
      }
      loadedRangeRef.current.from = Math.min(loadedRangeRef.current.from, pageFrom);

      // Prefetch next page back
      const nextFrom = pageFrom - PAGE_MS;
      const earliest = hmData.current[0]?.time ?? Infinity;
      if (nextFrom < earliest - PAGE_MS * 0.5) {
        setTimeout(() => { if (!dbLoadingRef.current) loadPageFromDB(nextFrom + PAGE_MS); }, 200);
      }
    } finally {
      dbLoadingRef.current = false;
    }
  }, []);

  // ── Normalization + CHANGE 3b: Downsampler (runs every 30s) ──────────────
  useEffect(() => {
    const computeNorm = () => {
      const data = hmData.current;
      if (data.length < 1) return;
      const start = Math.max(0, data.length - 100);
      const colMaxes = [];
      for (let si = start; si < data.length; si++) {
        const lvls = data[si].levels;
        let mx = 0;
        for (let i = 1; i < lvls.length; i += 3) if (lvls[i] > mx) mx = lvls[i];
        if (mx > 0) colMaxes.push(mx);
      }
      if (!colMaxes.length) return;
      colMaxes.sort((a,b)=>a-b);
      const p95 = colMaxes[Math.floor(colMaxes.length*0.95)] || colMaxes[colMaxes.length-1];
      if (normRef.current === 0) normRef.current = p95;
      else normRef.current = normRef.current*0.92 + p95*0.08;
      if (normRef.current < 0.001) normRef.current = 0.001;
    };

    // CHANGE 3b: Downsampler — compresses snaps older than DOWNSAMPLE_AGE into
    // DOWNSAMPLE_BIN-ms buckets so memory stays bounded across a full day.
    const downsampleOld = () => {
      const cutoff = Date.now() - DOWNSAMPLE_AGE;
      const data = hmData.current;
      const splitIdx = lowerBound(data, cutoff);
      if (splitIdx < 2) return;

      const hot  = data.slice(splitIdx);  // recent — keep full resolution
      const cold = data.slice(0, splitIdx); // old — compress these

      const buckets = new Map();
      for (const snap of cold) {
        const key = Math.floor(snap.time / DOWNSAMPLE_BIN) * DOWNSAMPLE_BIN;
        if (!buckets.has(key)) buckets.set(key, { time: key, mid: snap.mid, acc: new Map(), count: 0 });
        const b = buckets.get(key);
        b.mid = (b.mid * b.count + snap.mid) / (b.count + 1);
        b.count++;
        for (let i = 0; i < snap.levels.length; i += 3) {
          const p = snap.levels[i], q = snap.levels[i+1], s = snap.levels[i+2];
          const pk = p * 10 + s; // encode price+side into single float key
          const ex = b.acc.get(pk);
          if (!ex || q > ex.q) b.acc.set(pk, { p, q, s }); // keep peak qty
        }
      }

      const compressed = [];
      buckets.forEach(b => {
        const tmp = [];
        b.acc.forEach(({ p, q, s }) => tmp.push(p, q, s));
        compressed.push({ time: b.time, mid: b.mid, levels: new Float32Array(tmp) });
      });
      compressed.sort((a, b) => a.time - b.time);

      const before = hmData.current.length;
      hmData.current = [...compressed.slice(-ARCHIVE_SNAPS), ...hot];
      const after = hmData.current.length;
      if (before !== after) {
        hmDirty.current = true;
        console.log(`🗜️ Downsampled: ${before} → ${after} snaps`);
      }
    };

    // Run norm every 800ms, downsample every ~5s (every 7th tick)
    let normTick = 0;
    const iv = setInterval(() => {
      computeNorm();
      normTick++;
      if (normTick % 7 === 0) {
        downsampleOld();
        // Also trim tradesBuf to 24h and hard cap — runs in the same pass
        const tradeCutoff = Date.now() - MAX_DB_AGE;
        const tb = tradesBuf.current;
        const tradeKeep = lowerBound(tb, tradeCutoff);
        if (tradeKeep > 0) tradesBuf.current = tb.slice(tradeKeep);
        if (tradesBuf.current.length > MAX_TRADE_BUF)
          tradesBuf.current = tradesBuf.current.slice(-MAX_TRADE_BUF);
      }
    }, 800);

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        normRef.current = 0;
        computeNorm();
        const cutoff = Date.now() - HISTORY_SEC * 1000;
        const d = hmData.current;
        const keep = d.findIndex(s => s.time >= cutoff);
        if (keep > 0) hmData.current = d.slice(keep);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVisibility); };
  }, []);

  // ── IndexedDB: load on mount and on symbol switch ─────────────────────────
  useEffect(() => {
    const sym      = symbol;
    const now      = Date.now();
    const dbCutoff   = now - MAX_DB_AGE;
    const snapCutoff = now - HISTORY_SEC * 1000 - 60000;
    (async () => {
      try {
        const [savedSnaps, savedTrades, savedVol] = await Promise.all([
          dbLoadRange(sym, "snaps2", Math.max(dbCutoff, snapCutoff), MAX_SNAPS)
            .then(rows => rows.length ? rows : dbLoadRange(sym, "snaps", Math.max(dbCutoff, snapCutoff), MAX_SNAPS)),
          dbLoadRange(sym, "trades", Math.max(dbCutoff, now - MAX_DB_AGE), 50000),
          dbLoadRange(sym, "vol",    snapCutoff, 1000),
        ]);
        if (savedSnaps.length) {
          const restored = savedSnaps.map(unpackSnap);
          restored.sort((a,b)=>a.time-b.time);
          hmData.current = restored.slice(-MAX_SNAPS);
          const last = restored[restored.length-1];
          if (last) { mid.current=last.mid; setCurPrice(last.mid); }
          console.log(`📦 [${sym}] Restored ${restored.length} snapshots`);
        }
        if (savedTrades.length) {
          tradesBuf.current = savedTrades.sort((a,b)=>a.time-b.time);
          tc.current = savedTrades.length;
          setTrades24(tc.current);
        }
        if (savedVol.length) volHist.current = savedVol.sort((a,b)=>a.time-b.time);
        await Promise.all([
          dbDeleteOlderThan(sym, "snaps",  dbCutoff),
          dbDeleteOlderThan(sym, "snaps2", dbCutoff),
          dbDeleteOlderThan(sym, "trades", dbCutoff),
          dbDeleteOlderThan(sym, "vol",    dbCutoff),
        ]);
      } catch (e) { console.warn("DB load failed:", e); }
    })();
  }, [symbol]); // re-runs on symbol switch

  // ── IndexedDB: save every 8s ──────────────────────────────────────────────
  const lastSavedTime      = useRef(0);
  const lastSavedTradeTime = useRef(0);
  useEffect(() => {
    const iv = setInterval(async () => {
      const sym   = symbolRef.current;
      const snaps = hmData.current;
      const lo    = lowerBound(snaps, lastSavedTime.current + 1);
      const newSnaps = snaps.slice(lo);
      if (newSnaps.length) {
        lastSavedTime.current = newSnaps[newSnaps.length-1].time;
        await dbPutBatch(sym, "snaps2", newSnaps.map(packSnap));
      }
      const trades = tradesBuf.current;
      const tlo    = lowerBound(trades, lastSavedTradeTime.current + 1);
      const newTrades = trades.slice(tlo);
      if (newTrades.length) {
        lastSavedTradeTime.current = newTrades[newTrades.length-1].time;
        await dbPutBatch(sym, "trades", newTrades);
      }
      const volSnap = volHist.current.slice(-300);
      await dbPutBatch(sym, "vol", volSnap);
    }, PERSIST_MS);
    return () => clearInterval(iv);
  }, []);

  // ── Feed setup — re-runs on source or symbol change ──────────────────────
  useEffect(() => {
    const sym = symbol;
    const onTrade = (t) => {
      // NOTE: always record — isLive only controls the viewport, not data capture
      tradesBuf.current.push(t);
      // Trim in small batches to avoid large array copy on every overflow
      if (tradesBuf.current.length > MAX_TRADE_BUF + 500)
        tradesBuf.current.splice(0, 500);
      mid.current = t.price;
      tc.current++;
      const now = Date.now(), b = volHist.current, last = b[b.length-1];
      if (last && now-last.time < 1000) {
        if (t.side==="buy") last.bv+=t.qty; else last.sv+=t.qty;
      } else {
        b.push({time:now, bv:t.side==="buy"?t.qty:0, sv:t.side==="sell"?t.qty:0});
        if (b.length > 600) b.splice(0,1);
      }
      prevMid.current = prevMid.current || t.price;
      const delta = t.price - prevMid.current;
      prevMid.current = t.price;
      if (Math.abs(delta)>0 || tc.current%10===0) { setPChange(delta); setCurPrice(t.price); }
      if (tc.current%50===0) setTrades24(tc.current);
    };

    const onDepth = (ob) => {
      curOB.current = ob;
      const newCount = ob.bids.size + ob.asks.size;
      if (Math.abs(newCount - (curOB.current._lastCount||0)) > 10) {
        curOB.current._lastCount = newCount;
        setObLevels(newCount);
      }
      // NOTE: always record — isLive only controls the viewport, not data capture
      if (mid.current===0) return;

      const cfg          = pairCfgRef.current;
      const snapRange    = cfg.snapRange;
      const captureRange = Math.max(snapRange, visRangeRef.current);
      const midP = mid.current;

      if (ob.bids.size > 800) ob.bids.forEach((_,p)=>{ if (p<midP-captureRange) ob.bids.delete(p); });
      if (ob.asks.size > 800) ob.asks.forEach((_,p)=>{ if (p>midP+captureRange) ob.asks.delete(p); });

      const tmpLevels = [];
      ob.bids.forEach((q,p)=>{ if (p>=midP-captureRange&&p<=midP+1&&q>0) tmpLevels.push(p,q,0); });
      ob.asks.forEach((q,p)=>{ if (p<=midP+captureRange&&p>=midP-1&&q>0) tmpLevels.push(p,q,1); });
      const levels = new Float32Array(tmpLevels);
      hmData.current.push({time:Date.now(), mid:mid.current, levels});
      if (hmData.current.length > MAX_SNAPS + ARCHIVE_SNAPS)
        hmData.current.splice(0, hmData.current.length - MAX_SNAPS - ARCHIVE_SNAPS);
      hmDirty.current = true;

      if (normRef.current === 0) {
        let mx = 0;
        for (let i=1;i<levels.length;i+=3) if (levels[i]>mx) mx=levels[i];
        if (mx>0) normRef.current=mx;
      }

      let bidVol=0, askVol=0;
      const vr = cfg.volRange;
      ob.bids.forEach((q,p)=>{ if (Math.abs(p-mid.current)<vr) bidVol+=q; });
      ob.asks.forEach((q,p)=>{ if (Math.abs(p-mid.current)<vr) askVol+=q; });
      const tot=bidVol+askVol;
      if (tot>0) setImbalance(bidVol/tot);
    };

    if (feedRef.current) feedRef.current.disconnect();
    if (source==="binance") {
      setStatus("connecting");
      const cfg = getPairCfg(sym);
      const f=new BinanceFeed(sym, cfg.snapRange, onTrade, onDepth);
      f.connect(); feedRef.current=f;
      const tmr=setTimeout(()=>{
        if (f.connected) setStatus("connected");
        else {
          f.disconnect(); setStatus("simulated");
          const s=new SimFeed(onTrade,onDepth); s.connect(); feedRef.current=s; setSource("sim");
        }
      }, 5000);
      return ()=>{ clearTimeout(tmr); feedRef.current?.disconnect(); };
    } else {
      const s=new SimFeed(onTrade,onDepth); s.connect(); feedRef.current=s;
      setStatus("simulated");
      return ()=>{ feedRef.current?.disconnect(); };
    }
  }, [source, symbol]);

  // ── MAIN DRAW ─────────────────────────────────────────────────────────────
  const drawAll = useCallback(() => {
    const hc=hRef.current, vc=vRef.current, cc=cobRef.current;
    if (!hc||!vc||!cc) return;

    const perfNow = performance.now();
    if (perfNow - lastRafTime.current < RAF_TARGET_MS) return;
    lastRafTime.current = perfNow;

    const dpr = Math.min(window.devicePixelRatio||1, 1.5);
    if (!hctxRef.current) hctxRef.current = hc.getContext("2d",{alpha:false});
    if (!vctxRef.current) vctxRef.current = vc.getContext("2d",{alpha:false});
    if (!cctxRef.current) cctxRef.current = cc.getContext("2d",{alpha:false});
    const hctx=hctxRef.current, vctx=vctxRef.current, cctx=cctxRef.current;
    const W  = hc.width/dpr|0, H  = hc.height/dpr|0;
    const VW = vc.width/dpr|0, VH = vc.height/dpr|0;
    const CW = cc.width/dpr|0, CH = cc.height/dpr|0;

    hctx.setTransform(dpr,0,0,dpr,0,0);
    vctx.setTransform(dpr,0,0,dpr,0,0);
    cctx.setTransform(dpr,0,0,dpr,0,0);

    const data = hmData.current;
    if (data.length<2||mid.current===0) {
      hctx.fillStyle=`rgb(${BG_R},${BG_G},${BG_B})`; hctx.fillRect(0,0,W,H);
      hctx.fillStyle="#445"; hctx.font="13px monospace"; hctx.textAlign="center";
      hctx.fillText("Waiting for market data…",W/2,H/2);
      vctx.fillStyle=`rgb(${BG_R},${BG_G},${BG_B})`; vctx.fillRect(0,0,VW,VH);
      cctx.fillStyle=`rgb(${BG_R},${BG_G},${BG_B})`; cctx.fillRect(0,0,CW,CH);
      return;
    }

    const now    = Date.now();
    const cfg    = pairCfgRef.current;
    const visMs  = (HISTORY_SEC*1000)/tZoom;
    const endT   = liveRef.current ? now : now-tOff;
    const startT = endT-visMs;
    const pRange = cfg.defaultPRange/pZoom;
    const ctrP   = mid.current+pOff;
    const minP   = ctrP-pRange/2;
    const maxP   = ctrP+pRange/2;
    const pxMs   = W/visMs;
    const pxD    = H/pRange;

    visRangeRef.current = Math.max(cfg.snapRange, pRange/2 + cfg.snapRange*0.1);
    hmGeom.current = { W, H, startT, visMs, minP, pRange };

    const nominalColW = Math.max(1, pxMs*SNAP_MS*1.02);
    const cellH = Math.max(0.5, pxD*0.5);

    const loIdx = lowerBound(data, startT-SNAP_MS*4);
    const hiIdx = lowerBound(data, endT+SNAP_MS*2);

    const renderKey = `${tZoom.toFixed(4)}|${pZoom.toFixed(4)}|${pOff.toFixed(1)}|${tOff.toFixed(0)}|${W}|${H}`;
    const panZoomChanged = renderKey !== lastRenderKey.current;
    const shouldRebuild  = hmDirty.current || panZoomChanged || !cachedImg.current
                           || (perfNow-lastHmTime.current > 2000);

    if (shouldRebuild && (panZoomChanged || perfNow-lastHmTime.current >= HM_REBUILD_MS)) {
      lastRenderKey.current = renderKey;
      lastHmTime.current    = perfNow;
      hmDirty.current       = false;

      const mxQ = normRef.current;
      if (mxQ <= 0) {
        hctx.fillStyle=`rgb(${BG_R},${BG_G},${BG_B})`; hctx.fillRect(0,0,W,H);
      } else {
        const noiseFloor = mxQ*0.01/Math.max(0.1,intRef.current);
        const logBase    = Math.log1p(mxQ/noiseFloor);

        const imgData = hctx.createImageData(W,H);
        const px      = imgData.data;
        for (let i=0;i<px.length;i+=4) { px[i]=BG_R; px[i+1]=BG_G; px[i+2]=BG_B; px[i+3]=255; }

        if (!rowLI_bid_ref.current || rowLI_bid_ref.current.length < H+2) {
          rowLI_bid_ref.current = new Uint8Array(H+4);
          rowLI_ask_ref.current = new Uint8Array(H+4);
          rowGN_bid_ref.current = new Float32Array(H+4);
          rowGN_ask_ref.current = new Float32Array(H+4);
        }
        const rowLI_bid = rowLI_bid_ref.current;
        const rowLI_ask = rowLI_ask_ref.current;
        const rowGN_bid = rowGN_bid_ref.current;
        const rowGN_ask = rowGN_ask_ref.current;

        const usedRows = usedRowsRef.current;
        let usedRowsLen = 0;

        // CHANGE 7: Stride skip — cap at 2000 rendered columns when zoomed out.
        // Without this, a full-day view tries to render 172,800 columns → freezes.
        const totalCols = hiIdx - loIdx;
        const stride    = Math.max(1, Math.floor(totalCols / 2000));

        for (let ci=loIdx; ci<hiIdx; ci+=stride) {
          const col  = data[ci];
          const xC   = (col.time-startT)*pxMs;
          // For stride > 1, widen each column to avoid gaps
          const nextTime = ci+stride<hiIdx ? data[ci+stride].time : col.time+SNAP_MS*stride;
          const gapMs    = Math.min(nextTime-col.time, SNAP_MS*stride*4);
          const colW     = Math.max(nominalColW*stride, gapMs*pxMs*0.5);
          const xL = Math.max(0,  Math.floor(xC-colW*0.5))|0;
          const xR = Math.min(W-1,Math.ceil(xC+colW*0.5))|0;
          if (xL>=W||xR<0) continue;

          const lvls = col.levels;
          let colMaxBid=noiseFloor, colMaxAsk=noiseFloor;
          for (let i=0;i<lvls.length;i+=3) {
            const qty=lvls[i+1], side=lvls[i+2];
            if (qty<noiseFloor) continue;
            if (side===0) { if (qty>colMaxBid) colMaxBid=qty; }
            else          { if (qty>colMaxAsk) colMaxAsk=qty; }
          }

          usedRowsLen=0;
          for (let i=0;i<lvls.length;i+=3) {
            const price=lvls[i], qty=lvls[i+1], side=lvls[i+2];
            if (price<minP||price>maxP) continue;
            if (qty<noiseFloor) continue;
            const colMax  = side===0?colMaxBid:colMaxAsk;
            const logQty  = Math.log1p(qty/noiseFloor);
            const localN  = logQty/Math.max(0.001,Math.log1p(colMax/noiseFloor));
            const globalN = Math.min(1,logQty/logBase);
            const n  = Math.sqrt(localN)*Math.pow(globalN,0.40);
            const li = Math.min(255,(n*255+0.5)|0);
            const row = (H-(price-minP)*pxD+0.5)|0;
            if (row<0||row>=H) continue;
            if (side===0) {
              if (li>rowLI_bid[row]) { rowLI_bid[row]=li; rowGN_bid[row]=globalN; usedRows[usedRowsLen++]=row|0x10000; }
            } else {
              if (li>rowLI_ask[row]) { rowLI_ask[row]=li; rowGN_ask[row]=globalN; usedRows[usedRowsLen++]=row; }
            }
          }

          for (let ui=0;ui<usedRowsLen;ui++) {
            const isBid = (usedRows[ui]&0x10000)!==0;
            const row   = usedRows[ui]&0xFFFF;
            const li    = isBid?rowLI_bid[row]:rowLI_ask[row];
            const globalN = isBid?rowGN_bid[row]:rowGN_ask[row];
            const lut   = isBid?BID_LUT:ASK_LUT;
            const lr=lut[li*3], lg=lut[li*3+1], lb=lut[li*3+2];
            const bright = lr+lg+lb;
            if (bright<=BG_R+BG_G+BG_B+3) continue;
            const dynCellH = Math.max(0.5,cellH*(0.5+globalN*3.5));
            const yT = Math.max(0,(row-dynCellH+0.5)|0);
            const yB = Math.min(H-1,(row+dynCellH+0.5)|0);
            for (let y=yT;y<=yB;y++) {
              const rowOff=y*W;
              for (let x=xL;x<=xR;x++) {
                const off=(rowOff+x)*4;
                if (bright>px[off]+px[off+1]+px[off+2]) { px[off]=lr; px[off+1]=lg; px[off+2]=lb; }
              }
            }
            if (isBid) { rowLI_bid[row]=0; rowGN_bid[row]=0; }
            else        { rowLI_ask[row]=0; rowGN_ask[row]=0; }
          }
        }

        hctx.putImageData(imgData, 0, 0);
        cachedImg.current = imgData;
      }
    } else if (cachedImg.current) {
      hctx.putImageData(cachedImg.current, 0, 0);
    }

    // ── Grid ──────────────────────────────────────────────────────────────
    // Pick the largest gridStep that still fits at least 3 lines in view
    const gSteps = cfg.gridSteps;
    let gStep = gSteps[0];
    for (const s of gSteps) { if (pRange/s >= 3) { gStep = s; break; } }
    hctx.strokeStyle="rgba(255,255,255,0.045)"; hctx.lineWidth=0.5;
    for (let p=Math.ceil(minP/gStep)*gStep;p<maxP;p+=gStep) {
      const y=H-(p-minP)*pxD;
      hctx.beginPath(); hctx.moveTo(0,y); hctx.lineTo(W,y); hctx.stroke();
    }
    const tStep=visMs>43200000?3600000:visMs>14400000?1800000:visMs>3600000?900000
              :visMs>180000?60000:visMs>60000?30000:visMs>20000?10000:visMs>5000?5000:visMs>1000?1000:500;
    for (let t=Math.ceil(startT/tStep)*tStep;t<endT;t+=tStep) {
      const x=(t-startT)*pxMs;
      hctx.beginPath(); hctx.moveTo(x,0); hctx.lineTo(x,H); hctx.stroke();
    }

    // ── Mid-price path ─────────────────────────────────────────────────────
    if (hiIdx-loIdx>1) {
      hctx.strokeStyle="rgba(255,215,0,0.55)"; hctx.lineWidth=1; hctx.setLineDash([3,4]);
      hctx.beginPath(); let first=true;
      for (let ci=loIdx;ci<hiIdx;ci++) {
        const c=data[ci];
        const x=(c.time-startT)*pxMs;
        const y=H-(c.mid-minP)*pxD;
        if (x<-nominalColW||x>W+nominalColW) continue;
        if (first) { hctx.moveTo(x,y); first=false; } else hctx.lineTo(x,y);
      }
      hctx.stroke(); hctx.setLineDash([]);
    }
    const curY=H-(mid.current-minP)*pxD;
    if (curY>=0&&curY<=H) {
      hctx.strokeStyle="rgba(255,215,0,0.1)"; hctx.lineWidth=1;
      hctx.beginPath(); hctx.moveTo(0,curY); hctx.lineTo(W,curY); hctx.stroke();
    }

    // ── Trade bubbles ──────────────────────────────────────────────────────
    const trades = tradesBuf.current;
    const tLo = lowerBound(trades, startT);
    const tHi = lowerBound(trades, endT);
    const tradeCount = tHi-tLo;

    if (tradeCount>0) {
      const sampleStep = Math.max(1, Math.floor(tradeCount/500));
      // Compute separate thresholds per side so a sell-heavy session
        // doesn't wipe out all buy bubbles (and vice versa)
        const buySamples  = []; const sellSamples = [];
        for (let i=tLo;i<tHi;i+=sampleStep) {
          const t=trades[i];
          if (t.price<minP||t.price>maxP) continue;
          if (t.side==="buy") buySamples.push(t.qty); else sellSamples.push(t.qty);
        }
        buySamples.sort((a,b)=>a-b); sellSamples.sort((a,b)=>a-b);
        const pct = 0.82; // 82nd percentile — slightly lower to catch more bubbles
        const buyThreshold  = buySamples.length  ? (buySamples [Math.floor(buySamples.length *pct)] ?? buySamples [buySamples.length -1]) : Infinity;
        const sellThreshold = sellSamples.length ? (sellSamples[Math.floor(sellSamples.length*pct)] ?? sellSamples[sellSamples.length-1]) : Infinity;

        const CLUSTER_MS = Math.max(500,2000/tZoom);
        const CLUSTER_P  = Math.max(5,25/tZoom);

        const used = tradeUsedRef.current;
        used.fill(0, 0, Math.min(tradeCount, MAX_TRADE_BUF));

        const clusters = [];
        const actualCount = Math.min(tradeCount, MAX_TRADE_BUF);

        for (let i=tLo;i<tLo+actualCount;i++) {
          const ii=i-tLo;
          if (used[ii]) continue;
          const a=trades[i];
          const thresh = a.side==="buy" ? buyThreshold : sellThreshold;
          if (a.qty<thresh) { used[ii]=1; continue; }
          if (a.price<minP||a.price>maxP) { used[ii]=1; continue; }
          let totalQty=a.qty, wSumP=a.price*a.qty, wSumT=a.time*a.qty, weightSum=a.qty;
          const side=a.side;
          for (let j=i+1;j<tLo+actualCount;j++) {
            const jj=j-tLo;
            if (used[jj]) continue;
            const b=trades[j];
            if (b.side!==side) continue;
            if (b.time-a.time>CLUSTER_MS) break;
            if (Math.abs(b.price-a.price)>CLUSTER_P) continue;
            totalQty+=b.qty; wSumP+=b.price*b.qty; wSumT+=b.time*b.qty; weightSum+=b.qty; used[jj]=1;
          }
          used[ii]=1;
          clusters.push({qty:totalQty,price:wSumP/weightSum,time:wSumT/weightSum,side});
        }

        const SLOT_PX  = Math.max(40,80/tZoom);
        const slotCount = Math.ceil(W/SLOT_PX)+1;
        const slotBid   = new Float32Array(slotCount*3);
        const slotAsk   = new Float32Array(slotCount*3);
        for (const c of clusters) {
          const x    = (c.time-startT)*pxMs;
          const slot = Math.min(slotCount-1,Math.max(0,Math.floor(x/SLOT_PX)));
          const arr  = c.side==="buy"?slotBid:slotAsk;
          const off  = slot*3;
          if (c.qty>arr[off]) { arr[off]=c.qty; arr[off+1]=c.price; arr[off+2]=c.time; }
        }
        // Draw buys and sells with independent caps so one side can't starve the other
        let drawnBuy=0, drawnSell=0;
        const MAX_PER_SIDE = 15;
        for (let slot=0;slot<slotCount;slot++) {
          if (drawnBuy>=MAX_PER_SIDE && drawnSell>=MAX_PER_SIDE) break;
          const off=slot*3;
          if (drawnBuy<MAX_PER_SIDE && slotBid[off]) {
            const x=(slotBid[off+2]-startT)*pxMs;
            const y=H-(slotBid[off+1]-minP)*pxD;
            const r=Math.max(4,Math.min(28,Math.sqrt(slotBid[off]/buyThreshold)*7));
            drawBubble(hctx,x,y,r,true);
            drawnBuy++;
          }
          if (drawnSell<MAX_PER_SIDE && slotAsk[off]) {
            const x=(slotAsk[off+2]-startT)*pxMs;
            const y=H-(slotAsk[off+1]-minP)*pxD;
            const r=Math.max(4,Math.min(28,Math.sqrt(slotAsk[off]/sellThreshold)*7));
            drawBubble(hctx,x,y,r,false);
            drawnSell++;
          }
        }
    }

    // ── Volume histogram ────────────────────────────────────────────────────
    vctx.fillStyle=`rgb(${BG_R},${BG_G},${BG_B})`; vctx.fillRect(0,0,VW,VH);
    const vb   = volHist.current;
    const vbLo = lowerBound(vb, startT);
    const vbHi = lowerBound(vb, endT);
    let volMax = 0.001;
    for (let vi=vbLo;vi<vbHi;vi++) { const b=vb[vi]; const tot=b.bv+b.sv; if (tot>volMax) volMax=tot; }
    const bW = Math.max(1.5, pxMs*1000);
    for (let vi=vbLo;vi<vbHi;vi++) {
      const b=vb[vi];
      const x=(b.time-startT)*pxMs;
      const tot=((b.bv+b.sv)/volMax)*(VH-4);
      const buy=(b.bv/(b.bv+b.sv+1e-9))*tot;
      vctx.fillStyle="rgba(0,210,100,0.72)"; vctx.fillRect(x-bW/2,VH-buy,bW-0.5,buy);
      vctx.fillStyle="rgba(255,55,50,0.72)"; vctx.fillRect(x-bW/2,VH-tot,bW-0.5,tot-buy);
    }
    vctx.strokeStyle="rgba(255,255,255,0.04)"; vctx.lineWidth=1;
    vctx.beginPath(); vctx.moveTo(0,0); vctx.lineTo(VW,0); vctx.stroke();

    // ── COB panel — throttled to ~10fps ─────────────────────────────────────
    cobFrame.current = (cobFrame.current+1)%6;
    if (cobFrame.current!==0) return;

    cctx.clearRect(0,0,CW,CH);
    cctx.fillStyle="rgb(5,9,18)"; cctx.fillRect(0,0,CW,CH);
    const ob=curOB.current;
    if (!ob.bids.size&&!ob.asks.size) return;

    const HEADER_H=22, MIN_ROW_H=18, MIN_GAP=2;
    const cobPxD=CH/pRange;
    // Use per-pair gridSteps for bucket sizing instead of hardcoded dollar increments
    const niceIncs = cfg.gridSteps.slice().reverse(); // smallest→largest
    let bucketSz=niceIncs[niceIncs.length-1];
    for (const inc of niceIncs) { if (cobPxD*inc>=MIN_ROW_H) { bucketSz=inc; break; } }
    const dec = cfg.decimals;
    const cobRowH=Math.max(MIN_ROW_H,cobPxD*bucketSz);
    const maxRows=Math.max(1,Math.floor((CH-HEADER_H)/(cobRowH+MIN_GAP)));

    const bidMap=new Map(), askMap=new Map();
    ob.bids.forEach((q,p)=>{
      if (p<minP-bucketSz*2||p>maxP+bucketSz*2) return;
      const key=Math.floor(p/bucketSz)*bucketSz;
      const ex=bidMap.get(key);
      if (ex) { ex.totalQ+=q; if (q>ex.maxQ){ex.maxQ=q;ex.labelP=p;} }
      else bidMap.set(key,{totalQ:q,maxQ:q,labelP:p,bucketMid:key+bucketSz/2});
    });
    ob.asks.forEach((q,p)=>{
      if (p<minP-bucketSz*2||p>maxP+bucketSz*2) return;
      const key=Math.floor(p/bucketSz)*bucketSz;
      const ex=askMap.get(key);
      if (ex) { ex.totalQ+=q; if (q>ex.maxQ){ex.maxQ=q;ex.labelP=p;} }
      else askMap.set(key,{totalQ:q,maxQ:q,labelP:p,bucketMid:key+bucketSz/2});
    });

    let sessionMax=normRef.current||0.001;
    bidMap.forEach(b=>{ if (b.totalQ>sessionMax) sessionMax=b.totalQ; });
    askMap.forEach(b=>{ if (b.totalQ>sessionMax) sessionMax=b.totalQ; });
    const sigThreshold=sessionMax*0.01;

    const bidBuckets=[], askBuckets=[];
    bidMap.forEach((b,key)=>{ if (b.totalQ>=sigThreshold) bidBuckets.push({...b,key}); });
    askMap.forEach((b,key)=>{ if (b.totalQ>=sigThreshold) askBuckets.push({...b,key}); });
    bidBuckets.sort((a,b)=>b.totalQ-a.totalQ);
    askBuckets.sort((a,b)=>b.totalQ-a.totalQ);
    const topBids=bidBuckets.slice(0,maxRows);
    const topAsks=askBuckets.slice(0,maxRows);

    const fontSize=Math.max(10,Math.min(14,cobRowH*0.55|0));
    const barH=Math.max(4,cobRowH-MIN_GAP);
    cctx.font=`${fontSize}px 'JetBrains Mono',monospace`;
    const showText=barH>=fontSize+2;
    const priceW=showText?cctx.measureText("88,888.8").width+6:0;
    const qtyW  =showText?cctx.measureText("8.88k").width+6:0;
    const barStartX=priceW;
    const barMaxW=Math.max(4,CW-priceW-qtyW);

    const drawRow=(bucketMid,labelP,totalQ,isBid)=>{
      const rowY=CH-(bucketMid-minP)*cobPxD;
      if (rowY<HEADER_H-barH||rowY>CH+barH) return;
      const n=Math.sqrt(Math.min(1,totalQ/sessionMax));
      const bW=Math.max(3,n*barMaxW);
      const rTop=rowY-barH/2;
      cctx.fillStyle=isBid?`rgba(0,160,70,${0.03+n*0.09})`:`rgba(200,35,12,${0.03+n*0.09})`;
      cctx.fillRect(0,rTop,CW,barH);
      const g=cctx.createLinearGradient(barStartX,0,barStartX+bW,0);
      if (isBid) {
        g.addColorStop(0,`rgba(0,${70+n*120|0},${35+n*70|0},${0.35+n*0.55})`);
        g.addColorStop(1,`rgba(${n*40|0},${130+n*125|0},${55+n*140|0},${0.65+n*0.33})`);
      } else {
        g.addColorStop(0,`rgba(${75+n*135|0},${n*25|0},0,${0.35+n*0.55})`);
        g.addColorStop(1,`rgba(${175+n*80|0},${n*120|0},0,${0.65+n*0.33})`);
      }
      cctx.fillStyle=g; cctx.fillRect(barStartX,rTop,bW,barH);
      if (n>0.4) {
        cctx.fillStyle=isBid?`rgba(40,255,140,${(n-0.4)*1.4})`:`rgba(255,175,15,${(n-0.4)*1.4})`;
        cctx.fillRect(barStartX+bW-2,rTop,2,barH);
      }
      if (showText) {
        const textY=rTop+barH/2+fontSize*0.36;
        cctx.textAlign="left";
        cctx.fillStyle=isBid?`rgba(25,${90+n*145|0},50,${0.5+n*0.5})`:`rgba(${130+n*125|0},${n*55|0},8,${0.5+n*0.5})`;
        cctx.fillText(fmtP(labelP, dec),3,textY);
        cctx.textAlign="right";
        cctx.fillStyle=isBid?`rgba(45,${210+n*45|0},${115+n*80|0},${0.6+n*0.4})`:`rgba(${225+n*30|0},${130+n*100|0},12,${0.6+n*0.4})`;
        cctx.fillText(fmtQ(totalQ),CW-3,textY);
      }
      cctx.strokeStyle="rgba(255,255,255,0.03)"; cctx.lineWidth=0.5;
      cctx.beginPath(); cctx.moveTo(0,rTop); cctx.lineTo(CW,rTop); cctx.stroke();
    };

    topBids.forEach(b=>drawRow(b.bucketMid,b.labelP,b.totalQ,true));
    topAsks.forEach(b=>drawRow(b.bucketMid,b.labelP,b.totalQ,false));

    const cobMidY=CH-(mid.current-minP)*cobPxD;
    if (cobMidY>=HEADER_H&&cobMidY<=CH) {
      cctx.strokeStyle="rgba(255,215,0,0.9)"; cctx.lineWidth=1.5;
      cctx.beginPath(); cctx.moveTo(0,cobMidY); cctx.lineTo(CW,cobMidY); cctx.stroke();
      const priceCol=pChange>=0?"#00d68f":"#ff4757";
      cctx.fillStyle="rgba(5,9,18,0.85)"; cctx.fillRect(2,cobMidY-13,CW-4,13);
      cctx.fillStyle=priceCol;
      cctx.font="bold 12px 'JetBrains Mono',monospace"; cctx.textAlign="center";
      cctx.fillText(fmtP(mid.current, dec),CW/2,cobMidY-3);
    }

    cctx.fillStyle="rgba(0,0,0,0.75)"; cctx.fillRect(0,0,CW,HEADER_H);
    cctx.fillStyle="#2a3f55"; cctx.font="bold 11px monospace"; cctx.textAlign="left";
    cctx.fillText("ORDER BOOK",5,15);
    cctx.fillStyle="#1a2a38"; cctx.textAlign="right";
    cctx.fillText(`${bucketSz>=1000?(bucketSz/1000)+"k":bucketSz}$/row`,CW-4,15);
    cctx.strokeStyle="rgba(255,255,255,0.07)"; cctx.lineWidth=1;
    cctx.beginPath(); cctx.moveTo(0,HEADER_H); cctx.lineTo(CW,HEADER_H); cctx.stroke();
    cctx.beginPath(); cctx.moveTo(0,0); cctx.lineTo(0,CH); cctx.stroke();

  }, [tZoom, pZoom, pOff, tOff, pChange]);

  // ── Render loop ───────────────────────────────────────────────────────────
  useEffect(() => {
    const loop = () => { drawAll(); afRef.current = requestAnimationFrame(loop); };
    afRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(afRef.current);
  }, [drawAll]);

  // ── Canvas resize ─────────────────────────────────────────────────────────
  useEffect(() => {
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio||1, 1.5);
      [hRef,vRef,cobRef].forEach(ref=>{
        const c=ref.current; if (!c) return;
        const r=c.parentElement.getBoundingClientRect();
        c.width=r.width*dpr; c.height=r.height*dpr;
        c.style.width=r.width+"px"; c.style.height=r.height+"px";
      });
      hctxRef.current=null; vctxRef.current=null; cctxRef.current=null;
      cachedImg.current=null;
    };
    resize(); window.addEventListener("resize",resize);
    return ()=>window.removeEventListener("resize",resize);
  }, []);

  // ── Interactions ──────────────────────────────────────────────────────────
  const onWheel  = useCallback(e=>{ e.preventDefault(); setTZoom(z=>Math.max(0.1,Math.min(2880,z*(e.deltaY>0?0.88:1.14)))); },[]);
  const onPWheel = useCallback(e=>{ e.preventDefault(); setPZoom(z=>Math.max(0.1,Math.min(12,z*(e.deltaY>0?0.88:1.14)))); },[]);
  const onDown   = useCallback(e=>{ panFlag.current=true; panAxis.current=null; panStart.current={x:e.clientX,y:e.clientY}; },[]);

  // CHANGE 5: Pan handler now triggers lazy DB loader when scrolling into the past
  const onMove = useCallback(e=>{
    if (!panFlag.current) return;
    const dx=e.clientX-panStart.current.x, dy=e.clientY-panStart.current.y;
    panStart.current={x:e.clientX,y:e.clientY};
    if (!panAxis.current&&(Math.abs(dx)>2||Math.abs(dy)>2))
      panAxis.current=Math.abs(dx)>=Math.abs(dy)?"h":"v";
    const hc=hRef.current; if (!hc) return;
    const dpr=window.devicePixelRatio||1, W=hc.width/dpr, H=hc.height/dpr;
    if (panAxis.current==="h") {
      const visMs=(HISTORY_SEC*1000)/tZoom;
      setTOff(o=>{
        const newOff = Math.max(0, o+dx*(visMs/W));
        // CHANGE 5: Check if panned view reaches before earliest loaded data
        const viewStart = Date.now() - newOff - visMs;
        const earliest  = hmData.current[0]?.time ?? Date.now();
        if (viewStart < earliest - PAGE_MS * 0.5) {
          loadPageFromDB(viewStart); // async, non-blocking
        }
        return newOff;
      });
      if (dx>0) setIsLive(false);
    } else if (panAxis.current==="v") {
      setPOff(o=>o+dy*((2000/pZoom)/H));
    }
  },[tZoom, pZoom, loadPageFromDB]);

  const onUp      = useCallback(()=>{ panFlag.current=false; panAxis.current=null; },[]);
  const onPAxDown = useCallback(e=>{ paxFlag.current=true; paxStartY.current=e.clientY; paxStartOff.current=pOff; e.preventDefault(); },[pOff]);
  const onPAxMove = useCallback(e=>{
    if (!paxFlag.current) return;
    const H=hRef.current?hRef.current.getBoundingClientRect().height:600;
    const pRange = getPairCfg(symbolRef.current).defaultPRange / pZoom;
    setPOff(paxStartOff.current+(e.clientY-paxStartY.current)*(pRange/H));
  },[pZoom]);
  const onPAxUp   = useCallback(()=>{ paxFlag.current=false; },[]);
  const jumpLive  = useCallback(()=>{ setIsLive(true); setTOff(0); setPOff(0); setActiveWindow("5m"); },[]);

  const openStorage = useCallback(async () => {
    setShowStorage(true);
    const stats = await dbGetAllStats();
    setStorageStats(stats);
  }, []);

  const clearAll = useCallback(async () => {
    if (!window.confirm("Delete ALL stored data for ALL pairs? This cannot be undone.")) return;
    setClearingAll(true);
    for (const pair of PAIRS) await dbClearSymbol(pair.symbol);
    hmData.current    = [];
    tradesBuf.current = [];
    volHist.current   = [];
    normRef.current   = 0;
    cachedImg.current = null;
    hmDirty.current   = true;
    lastSavedTime.current      = 0;
    lastSavedTradeTime.current = 0;
    setClearingAll(false);
    setStorageStats({ rows: [], realUsage: null });
  }, []);

  useEffect(()=>{
    const h=e=>{ if (e.key===" ") { e.preventDefault(); setIsLive(l=>!l); } if (e.key==="l") jumpLive(); };
    window.addEventListener("keydown",h);
    return ()=>window.removeEventListener("keydown",h);
  },[jumpLive]);

  // ── Labels ────────────────────────────────────────────────────────────────
  const pcCol = pChange>=0?"#00d68f":"#ff4757";
  const stCol = status==="connected"?"#00d68f":status==="connecting"?"#ffb800":"#667788";

  const priceLabels = (()=>{
    const cfg = getPairCfg(symbol);
    const mp  = curPrice || 0;
    const pR  = cfg.defaultPRange / pZoom;
    const ctr = mp + pOff, mn = ctr - pR/2, mx = ctr + pR/2;
    // Use same gridSteps logic as drawAll
    let step = cfg.gridSteps[0];
    for (const s of cfg.gridSteps) { if (pR/s >= 3) { step = s; break; } }
    const out=[];
    // Use floating-point safe step to avoid drift on small prices
    let p = Math.ceil(mn / step) * step;
    // Round to avoid floating-point noise (e.g. 0.10000000001)
    const dp = Math.max(0, -Math.floor(Math.log10(step))) + 1;
    while (p < mx) {
      out.push({ price: parseFloat(p.toFixed(dp)), pct: 100-((p-mn)/pR)*100 });
      p += step;
    }
    return out;
  })();

  const visMs = (HISTORY_SEC*1000)/tZoom;
  const timeLabels = (()=>{
    const now=Date.now();
    const end=isLive?now:now-tOff, start=end-visMs;
    // CHANGE 6c: Adaptive time step across all zoom levels
    const step=visMs>43200000?3600000:visMs>14400000?1800000:visMs>3600000?900000
              :visMs>180000?60000:visMs>60000?30000:visMs>20000?10000:visMs>5000?5000:visMs>1000?1000:500;
    const out=[];
    for (let t=Math.ceil(start/step)*step;t<end;t+=step) out.push({time:t,pct:((t-start)/visMs)*100});
    return out;
  })();

  const zb={width:22,height:22,display:"flex",alignItems:"center",justifyContent:"center",
    fontSize:14,fontFamily:"inherit",background:"rgba(255,255,255,0.05)",
    border:"1px solid rgba(255,255,255,0.08)",borderRadius:3,color:"#778",cursor:"pointer",padding:0,lineHeight:1};

  // CHANGE 6d: Time window presets — 1s / 1m / 5m / 1h / 4h / 1D
  const TIME_WINDOWS = [
    { label:"1s",  sec:30    },  // 30s visible window — enough to see order flow at tick level
    { label:"1m",  sec:60    },  // 1min visible window
    { label:"5m",  sec:300   },
    { label:"1h",  sec:3600  },
    { label:"4h",  sec:14400 },
    { label:"1D",  sec:86400 },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{width:"100vw",height:"100vh",background:`rgb(${BG_R},${BG_G},${BG_B})`,color:"#c8d0e0",
      fontFamily:"'JetBrains Mono','Fira Code','SF Mono',monospace",display:"flex",flexDirection:"column",
      overflow:"hidden",userSelect:"none"}}>

      {/* Toolbar */}
      <div style={{height:52,background:"linear-gradient(180deg,#0f1520,#0a1015)",
        borderBottom:"1px solid rgba(255,255,255,0.07)",display:"flex",alignItems:"center",
        padding:"0 14px",gap:12,flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          <div style={{width:7,height:7,borderRadius:"50%",background:stCol,boxShadow:`0 0 8px ${stCol}`}}/>
          <span style={{fontSize:12,fontWeight:700,letterSpacing:1.5,color:"#dde6f2"}}>LIQUIDITY HEATMAP</span>
          <span style={{fontSize:10,color:"#556",padding:"2px 6px",background:"rgba(255,255,255,0.04)",borderRadius:3}}>{symbol} PERP</span>
        </div>

        {/* Pair selector */}
        <div style={{display:"flex",gap:2,background:"rgba(255,255,255,0.03)",borderRadius:5,padding:"3px 4px"}}>
          {PAIRS.map(p=>(
            <button key={p.symbol} onClick={()=>switchSymbol(p.symbol)} style={{
              padding:"3px 9px", fontSize:11, fontFamily:"inherit", fontWeight:700,
              background: symbol===p.symbol ? "rgba(255,200,0,0.18)" : "rgba(255,255,255,0.02)",
              border:`1px solid ${symbol===p.symbol?"rgba(255,200,0,0.4)":"rgba(255,255,255,0.06)"}`,
              borderRadius:3, color: symbol===p.symbol ? "#ffc800" : "#556677",
              cursor:"pointer", transition:"all 0.15s",
            }}>{p.label}</button>
          ))}
        </div>

        <div style={{display:"flex",alignItems:"baseline",gap:8,marginLeft:4}}>
          <span style={{fontSize:20,fontWeight:700,color:pcCol,textShadow:`0 0 18px ${pcCol}40`}}>
            {curPrice>0?fmtP(curPrice, getPairCfg(symbol).decimals):"—"}
          </span>
          <span style={{fontSize:11,color:pcCol,opacity:0.75}}>{pChange>=0?"▲":"▼"} {Math.abs(pChange).toFixed(getPairCfg(symbol).decimals)}</span>
        </div>
        {obLevels>0&&<span style={{fontSize:10,color:"#3a4d5e",padding:"2px 5px",background:"rgba(255,255,255,0.02)",borderRadius:2}}>{obLevels} lvls</span>}
        <div style={{flex:1}}/>

        {/* Load History */}
        <div style={{display:"flex",alignItems:"center",gap:4}}>
          <span style={{fontSize:9,color:"#445",letterSpacing:1}}>LOAD</span>
          {[1,5,12,24].map(h=>(
            <button key={h} onClick={()=>loadHistoryRange(h)}
              disabled={loadingHistory}
              style={{
                padding:"3px 9px", fontSize:10, fontFamily:"inherit", fontWeight:700,
                background: loadedHours>=h ? "rgba(0,180,90,0.15)" : "rgba(255,255,255,0.04)",
                border:`1px solid ${loadedHours>=h?"rgba(0,180,90,0.3)":"rgba(255,255,255,0.08)"}`,
                borderRadius:3,
                color: loadedHours>=h ? "#00d68f" : loadingHistory ? "#334" : "#667788",
                cursor: loadingHistory ? "wait" : "pointer",
                transition:"all 0.2s",
              }}>
              {loadingHistory && loadedHours < h && loadedHours > h-1
                ? `${Math.round((loadedHours % 1)*100)}%`
                : `${h}h`}
            </button>
          ))}
        </div>

        {/* Time window */}
        <div style={{display:"flex",gap:2,background:"rgba(255,255,255,0.03)",borderRadius:5,padding:"3px 4px"}}>
          {TIME_WINDOWS.map(({label, sec})=>(
            <button key={label}
              onClick={()=>{
                const newZoom = HISTORY_SEC / sec;
                setTZoom(newZoom); setActiveWindow(label); setIsLive(false);
                if (sec > 600) loadPageFromDB(Date.now() - sec * 1000);
              }}
              style={{
                padding:"3px 9px", fontSize:10, fontFamily:"inherit", fontWeight:700,
                background: activeWindow===label ? "rgba(74,158,255,0.22)" : "rgba(255,255,255,0.03)",
                border:`1px solid ${activeWindow===label?"rgba(74,158,255,0.5)":"rgba(255,255,255,0.07)"}`,
                borderRadius:3, color: activeWindow===label ? "#4a9eff" : "#556677",
                cursor:"pointer", letterSpacing:0.5, transition:"all 0.15s",
              }}>
              {label}
            </button>
          ))}
        </div>

        <button onClick={()=>setSource(s=>s==="binance"?"sim":"binance")} style={{
          padding:"4px 11px",fontSize:10,fontFamily:"inherit",
          background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",
          borderRadius:4,color:"#7888a0",cursor:"pointer"}}>
          {source==="binance"?"⚡ FUTURES":"◆ SIM"}
        </button>

        <div style={{display:"flex",alignItems:"center",gap:5}}>
          <span style={{fontSize:9,color:"#556"}}>INT</span>
          <input type="range" min="0.3" max="5" step="0.1" value={intensity}
            onChange={e=>setIntensity(parseFloat(e.target.value))}
            style={{width:64,height:3,appearance:"none",background:"rgba(255,255,255,0.1)",borderRadius:2,outline:"none",cursor:"pointer",accentColor:"#4a9eff"}}/>
        </div>

        <div style={{display:"flex",alignItems:"center",gap:3,background:"rgba(255,255,255,0.03)",borderRadius:4,padding:"3px 5px"}}>
          <span style={{fontSize:9,color:"#556",marginRight:2}}>T</span>
          <button onClick={()=>setTZoom(z=>Math.min(2880,z*1.3))} style={zb}>+</button>
          <button onClick={()=>setTZoom(z=>Math.max(0.1,z/1.3))} style={zb}>−</button>
          <span style={{fontSize:9,color:"#556",marginLeft:5,marginRight:2}}>P</span>
          <button onClick={()=>setPZoom(z=>Math.min(12,z*1.3))} style={zb}>+</button>
          <button onClick={()=>setPZoom(z=>Math.max(0.1,z/1.3))} style={zb}>−</button>
        </div>

        <button onClick={openStorage} style={{padding:"4px 10px",fontSize:10,fontFamily:"inherit",
          background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.09)",
          borderRadius:4,color:"#556677",cursor:"pointer",letterSpacing:0.5}}>
          💾 DB
        </button>

        <button onClick={()=>setIsLive(l=>!l)} style={{padding:"4px 12px",fontSize:10,fontFamily:"inherit",fontWeight:700,
          background:isLive?"linear-gradient(135deg,rgba(0,180,90,0.28),rgba(0,130,60,0.14))":"linear-gradient(135deg,rgba(255,180,0,0.28),rgba(200,130,0,0.14))",
          border:`1px solid ${isLive?"rgba(0,180,90,0.4)":"rgba(255,180,0,0.4)"}`,
          borderRadius:4,color:isLive?"#00d68f":"#ffb800",cursor:"pointer",letterSpacing:0.8}}>
          {isLive?"● LIVE":"❚❚ PAUSE"}
        </button>
        {!isLive&&<button onClick={jumpLive} style={{padding:"4px 10px",fontSize:10,fontFamily:"inherit",
          background:"rgba(74,158,255,0.13)",border:"1px solid rgba(74,158,255,0.28)",
          borderRadius:4,color:"#4a9eff",cursor:"pointer"}}>⟫ LIVE</button>}
      </div>

      {/* Content */}
      <div style={{flex:1,display:"flex",overflow:"hidden"}}>

        {/* Heatmap + volume + time */}
        <div style={{flex:1,display:"flex",flexDirection:"column",minWidth:0}}>
          <div style={{flex:1,position:"relative",cursor:"crosshair",minHeight:0}}
            onWheel={onWheel} onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}>
            <canvas ref={hRef} style={{width:"100%",height:"100%",display:"block"}}/>
            <div style={{position:"absolute",top:8,left:12,fontSize:8,color:"rgba(255,255,255,0.04)",
              letterSpacing:2,fontWeight:700,pointerEvents:"none"}}>
              {source==="binance"?"BINANCE FUTURES":"SIMULATED"} · {symbol} PERP
            </div>
            {/* Loading history progress overlay */}
            {loadingHistory&&(
              <div style={{position:"absolute",bottom:8,left:"50%",transform:"translateX(-50%)",
                fontSize:9,color:"rgba(0,214,143,0.9)",background:"rgba(5,9,18,0.9)",
                padding:"4px 14px",borderRadius:4,border:"1px solid rgba(0,214,143,0.25)",
                pointerEvents:"none",letterSpacing:0.5}}>
                ⏳ Loading {Math.floor(loadedHours * 10) / 10}h of history…
              </div>
            )}
          </div>
          <div style={{height:VOL_H,flexShrink:0,position:"relative"}}>
            <canvas ref={vRef} style={{width:"100%",height:"100%",display:"block"}}/>
            <div style={{position:"absolute",top:3,left:6,fontSize:7,color:"rgba(255,255,255,0.09)"}}>VOLUME</div>
          </div>
          <div style={{height:TIME_AXIS_H,flexShrink:0,background:"#08101a",
            borderTop:"1px solid rgba(255,255,255,0.06)",position:"relative",overflow:"hidden"}}>
            {(()=>{
              // Estimate label width in pixels based on format used at this zoom
              const labelPx = visMs > 3600000 ? 90 : visMs < 10000 ? 100 : 70;
              const hc = hRef.current;
              const W  = hc ? hc.getBoundingClientRect().width : window.innerWidth - PRICE_AXIS_W - COB_W;
              const minGapPct = (labelPx / W) * 100;
              let lastPct = -999;
              return timeLabels.map((l,i) => {
                if (l.pct - lastPct < minGapPct) return null;
                lastPct = l.pct;
                return (
                  <div key={i} style={{position:"absolute",left:`${l.pct}%`,top:8,transform:"translateX(-50%)",
                    fontSize:11,color:"#5a7088",whiteSpace:"nowrap",fontWeight:500}}>
                    {fmtT(l.time, visMs)}
                  </div>
                );
              });
            })()}
          </div>
        </div>

        {/* Price axis */}
        <div style={{width:PRICE_AXIS_W,flexShrink:0,background:"#08101a",
          borderLeft:"1px solid rgba(255,255,255,0.06)",position:"relative",overflow:"hidden",cursor:"ns-resize"}}
          onWheel={onPWheel} onMouseDown={onPAxDown} onMouseMove={onPAxMove} onMouseUp={onPAxUp} onMouseLeave={onPAxUp}>
          {priceLabels.map((l,i)=>(
            <div key={i} style={{position:"absolute",top:`${l.pct}%`,right:6,transform:"translateY(-50%)",
              fontSize:12,color:"#3a4a5a",whiteSpace:"nowrap"}}>
              {fmtP(l.price, getPairCfg(symbol).decimals)}
            </div>
          ))}
          {curPrice>0&&(()=>{
            const cfg2=getPairCfg(symbol);
            const pR=cfg2.defaultPRange/pZoom, ctr=curPrice+pOff, mn=ctr-pR/2;
            const pct=100-((curPrice-mn)/pR)*100;
            if (pct<0||pct>100) return null;
            return (
              <div style={{position:"absolute",top:`${pct}%`,left:0,right:0,transform:"translateY(-50%)",pointerEvents:"none"}}>
                <div style={{background:`linear-gradient(90deg,${pcCol}14,${pcCol}cc)`,padding:"2px 5px",
                  fontSize:9,fontWeight:700,color:"#fff",textAlign:"right",
                  borderLeft:`2px solid ${pcCol}`,textShadow:`0 0 7px ${pcCol}`}}>
                  {fmtP(curPrice, cfg2.decimals)}
                </div>
              </div>
            );
          })()}
        </div>

        {/* COB */}
        <div style={{width:COB_W,flexShrink:0,background:"rgb(5,9,18)",display:"flex",flexDirection:"column",overflow:"hidden"}}>
          <div style={{flex:1,position:"relative",minHeight:0}}>
            <canvas ref={cobRef} style={{width:"100%",height:"100%",display:"block"}}/>
          </div>
          <div style={{height:VOL_H+TIME_AXIS_H,flexShrink:0,background:"#060e18",
            borderTop:"1px solid rgba(255,255,255,0.06)",padding:"6px 8px",
            display:"flex",flexDirection:"column",gap:3}}>
            <div style={{fontSize:11,color:"#2a3a4a",letterSpacing:1.5,fontWeight:700,marginBottom:1}}>LIMIT ORDERS</div>
            <div style={{display:"flex",alignItems:"center",gap:4,marginBottom:2}}>
              <div style={{flex:1,height:5,background:"rgba(255,255,255,0.05)",borderRadius:2,overflow:"hidden",display:"flex"}}>
                <div style={{width:`${imbalance*100}%`,background:"linear-gradient(90deg,#0a3a1a,#00cc66)",borderRadius:"2px 0 0 2px",transition:"width 0.4s"}}/>
                <div style={{flex:1,background:"linear-gradient(90deg,#cc2200,#3a0a0a)",borderRadius:"0 2px 2px 0"}}/>
              </div>
              <span style={{fontSize:10,color:"#223344",whiteSpace:"nowrap"}}>{Math.round(imbalance*100)}B/{Math.round((1-imbalance)*100)}A</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
              <span style={{color:"#0d4a28"}}>BIDS</span>
              <span style={{color:"#1a6638",fontVariantNumeric:"tabular-nums"}}>{curOB.current.bids.size} orders</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:12}}>
              <span style={{color:"#4a1a0d"}}>ASKS</span>
              <span style={{color:"#662222",fontVariantNumeric:"tabular-nums"}}>{curOB.current.asks.size} orders</span>
            </div>
            <div style={{borderTop:"1px solid rgba(255,255,255,0.04)",marginTop:2,paddingTop:3,
              display:"flex",justifyContent:"space-between",fontSize:10,color:"#223"}}>
              <span>trades: {trades24}</span>
              <span style={{color:stCol}}>{status==="connected"?"⚡ LIVE":status==="connecting"?"⏳":"◆ SIM"}</span>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:9,color:"#1a2a3a",marginTop:1}}>
              <span>💾 {hmData.current.length} snaps</span>
              <span style={{color:"#1a3a2a"}}>±{getPairCfg(symbol).snapRange}</span>
            </div>
          </div>
        </div>

      </div>

      {/* ── Storage Panel ─────────────────────────────────────────────────── */}
      {showStorage && (
        <div onClick={()=>setShowStorage(false)} style={{
          position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",display:"flex",
          alignItems:"center",justifyContent:"center",zIndex:9999,backdropFilter:"blur(4px)"}}>
          <div onClick={e=>e.stopPropagation()} style={{
            background:"#0b1220",border:"1px solid rgba(255,255,255,0.1)",borderRadius:10,
            padding:"26px 30px",minWidth:620,maxWidth:700,fontFamily:"'JetBrains Mono',monospace",
            boxShadow:"0 24px 80px rgba(0,0,0,0.9)"}}>

            {/* Header */}
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:22}}>
              <div style={{display:"flex",alignItems:"center",gap:10}}>
                <span style={{fontSize:14,fontWeight:700,letterSpacing:1.5,color:"#c8d0e0"}}>💾 STORED DATA</span>
                {storageStats.realUsage != null && (
                  <span style={{fontSize:10,color:"#4a9eff",background:"rgba(74,158,255,0.1)",
                    border:"1px solid rgba(74,158,255,0.2)",borderRadius:3,padding:"2px 8px"}}>
                    {fmtBytes(storageStats.realUsage)} actual (browser)
                  </span>
                )}
              </div>
              <button onClick={()=>setShowStorage(false)} style={{
                background:"none",border:"none",color:"#445",fontSize:20,cursor:"pointer",lineHeight:1}}>✕</button>
            </div>

            {/* Table */}
            {!storageStats.rows || storageStats.rows.length === 0 ? (
              <div style={{color:"#334",fontSize:12,textAlign:"center",padding:"28px 0"}}>
                No data stored yet
              </div>
            ) : (
              <div style={{display:"flex",flexDirection:"column",gap:2}}>
                {/* Column headers */}
                <div style={{display:"grid",gridTemplateColumns:"52px 1fr 70px 70px 70px 80px 64px",
                  gap:"0 12px",padding:"0 0 8px",borderBottom:"1px solid rgba(255,255,255,0.07)",
                  fontSize:9,color:"#2e4455",letterSpacing:1,fontWeight:600}}>
                  <span>PAIR</span>
                  <span>RECORDED</span>
                  <span style={{textAlign:"right"}}>SNAPS</span>
                  <span style={{textAlign:"right"}}>TRADES</span>
                  <span style={{textAlign:"right"}}>ROWS</span>
                  <span style={{textAlign:"right"}}>EST SIZE</span>
                  <span/>
                </div>
                {storageStats.rows.map(s => (
                  <div key={s.sym} style={{display:"grid",
                    gridTemplateColumns:"52px 1fr 70px 70px 70px 80px 64px",
                    gap:"0 12px",padding:"10px 0",
                    borderBottom:"1px solid rgba(255,255,255,0.03)",alignItems:"center"}}>
                    {/* Pair */}
                    <span style={{color:"#ffc800",fontWeight:700,fontSize:12}}>{s.label}</span>
                    {/* Time range */}
                    <div style={{display:"flex",flexDirection:"column",gap:2}}>
                      <span style={{fontSize:10,color:"#5a8899",fontVariantNumeric:"tabular-nums"}}>
                        {s.firstTs ? new Date(s.firstTs).toLocaleString("en-US",{hour12:false,month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}
                        <span style={{color:"#2a3a4a",margin:"0 5px"}}>→</span>
                        {s.lastTs  ? new Date(s.lastTs).toLocaleString("en-US",{hour12:false,month:"2-digit",day:"2-digit",hour:"2-digit",minute:"2-digit"}) : "—"}
                      </span>
                      {s.firstTs && s.lastTs && (
                        <span style={{fontSize:9,color:"#2a5060"}}>
                          {((s.lastTs - s.firstTs)/3600000).toFixed(1)}h of history
                        </span>
                      )}
                    </div>
                    {/* Snaps */}
                    <span style={{textAlign:"right",color:"#4a9eff",fontSize:11,fontVariantNumeric:"tabular-nums"}}>
                      {s.snaps.toLocaleString()}
                    </span>
                    {/* Trades */}
                    <span style={{textAlign:"right",color:"#00d68f",fontSize:11,fontVariantNumeric:"tabular-nums"}}>
                      {s.trades.toLocaleString()}
                    </span>
                    {/* Total rows */}
                    <span style={{textAlign:"right",color:"#c8d0e0",fontSize:11,fontWeight:600,fontVariantNumeric:"tabular-nums"}}>
                      {s.total.toLocaleString()}
                    </span>
                    {/* Est size */}
                    <span style={{textAlign:"right",color:"#889aaa",fontSize:11}}>
                      ~{fmtBytes(s.estBytes)}
                    </span>
                    {/* Clear */}
                    <button onClick={async()=>{
                      if (!window.confirm(`Clear all data for ${s.label}?`)) return;
                      await dbClearSymbol(s.sym);
                      if (s.sym === symbolRef.current) {
                        hmData.current=[]; tradesBuf.current=[]; volHist.current=[];
                        normRef.current=0; cachedImg.current=null; hmDirty.current=true;
                        lastSavedTime.current=0; lastSavedTradeTime.current=0;
                      }
                      const updated = await dbGetAllStats();
                      setStorageStats(updated);
                    }} style={{padding:"3px 8px",fontSize:9,fontFamily:"inherit",
                      background:"rgba(255,60,60,0.1)",border:"1px solid rgba(255,60,60,0.25)",
                      borderRadius:3,color:"#ff5555",cursor:"pointer",letterSpacing:0.5}}>CLEAR</button>
                  </div>
                ))}
              </div>
            )}

            {/* Footer */}
            <div style={{marginTop:20,paddingTop:16,borderTop:"1px solid rgba(255,255,255,0.06)",
              display:"flex",alignItems:"center",justifyContent:"space-between"}}>
              <div style={{display:"flex",flexDirection:"column",gap:3}}>
                <span style={{fontSize:10,color:"#2a3a4a"}}>
                  {(storageStats.rows||[]).length} pair{(storageStats.rows||[]).length!==1?"s":""} stored ·{" "}
                  {(storageStats.rows||[]).reduce((a,b)=>a+b.total,0).toLocaleString()} total rows ·{" "}
                  ~{fmtBytes((storageStats.rows||[]).reduce((a,b)=>a+b.estBytes,0))} estimated
                </span>
                {storageStats.realUsage != null && (
                  <span style={{fontSize:9,color:"#1a2a3a"}}>
                    Browser reports {fmtBytes(storageStats.realUsage)} used by this origin (all IndexedDB + cache)
                  </span>
                )}
              </div>
              <div style={{display:"flex",gap:8}}>
                <button onClick={async()=>{
                  const stats = await dbGetAllStats();
                  setStorageStats(stats);
                }} style={{padding:"4px 12px",fontSize:10,fontFamily:"inherit",
                  background:"rgba(255,255,255,0.05)",border:"1px solid rgba(255,255,255,0.1)",
                  borderRadius:4,color:"#778",cursor:"pointer"}}>↻ REFRESH</button>
                <button onClick={clearAll} disabled={clearingAll} style={{
                  padding:"4px 14px",fontSize:10,fontFamily:"inherit",fontWeight:700,
                  background:"rgba(255,40,40,0.14)",border:"1px solid rgba(255,40,40,0.3)",
                  borderRadius:4,color:clearingAll?"#444":"#ff4444",cursor:clearingAll?"wait":"pointer",
                  letterSpacing:0.5}}>
                  {clearingAll?"CLEARING…":"🗑 CLEAR ALL"}
                </button>
              </div>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}